import React, { useState, useEffect } from 'react';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Icon from '../../components/AppIcon';
import incrementLettersService from '../../services/incrementLettersService';
import employeeService from '../../services/employeeService';
import NotificationToast from '../../components/ui/NotificationToast';

const IncrementLetters = () => {
  const [letters, setLetters] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [previewContent, setPreviewContent] = useState('');
  const [previewUrl, setPreviewUrl] = useState('');
  const [editingLetter, setEditingLetter] = useState(null);
  const [notification, setNotification] = useState(null);
  const [generatingPreview, setGeneratingPreview] = useState(false);
  const [formData, setFormData] = useState({
    employee_id: '', previous_salary: '', new_salary: '', increment_percentage: '',
    effective_date: '', reason: '', content: '', status: 'draft'
  });

  useEffect(() => {
    loadLetters();
    loadEmployees();
  }, []);
  
  // Cleanup blob URL when preview closes or component unmounts
  useEffect(() => {
    return () => {
      if (previewUrl && previewUrl.startsWith('blob:')) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  const loadLetters = async () => {
    try {
      setLoading(true);
      const result = await incrementLettersService.getIncrementLetters();
      if (result?.success) setLetters(result.data || []);
    } catch (error) {
      console.error('Error loading increment letters:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadEmployees = async () => {
    const result = await employeeService.getEmployees();
    if (result?.success) setEmployees(result.data || []);
  };

  useEffect(() => {
    if (formData.previous_salary && formData.new_salary) {
      const prev = parseFloat(formData.previous_salary);
      const newSal = parseFloat(formData.new_salary);
      if (prev > 0) {
        const percentage = ((newSal - prev) / prev) * 100;
        setFormData(prev => ({...prev, increment_percentage: percentage.toFixed(2)}));
      }
    }
  }, [formData.previous_salary, formData.new_salary]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const result = editingLetter
        ? await incrementLettersService.updateIncrementLetter(editingLetter.id, formData)
        : await incrementLettersService.createIncrementLetter(formData);
      
      if (result?.success) {
        setNotification({ message: `Increment letter ${editingLetter ? 'updated' : 'created'} successfully`, type: 'success' });
        setShowModal(false);
        setEditingLetter(null);
        setFormData({ employee_id: '', previous_salary: '', new_salary: '', increment_percentage: '', effective_date: '', reason: '', content: '', status: 'draft' });
        loadLetters();
      } else {
        setNotification({ message: result?.error || 'Operation failed', type: 'error' });
      }
    } catch (error) {
      setNotification({ message: 'An error occurred', type: 'error' });
    }
  };

  const handleEdit = (letter) => {
    setEditingLetter(letter);
    setFormData({
      employee_id: letter.employee_id || '',
      previous_salary: letter.previous_salary || '',
      new_salary: letter.new_salary || '',
      increment_percentage: letter.increment_percentage || '',
      effective_date: letter.effective_date || '',
      reason: letter.reason || '',
      content: letter.content || '',
      status: letter.status || 'draft'
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this increment letter?')) return;
    try {
      const result = await incrementLettersService.deleteIncrementLetter(id);
      if (result?.success) {
        setNotification({ message: 'Increment letter deleted successfully', type: 'success' });
        loadLetters();
      } else {
        setNotification({ message: result?.error || 'Failed to delete', type: 'error' });
      }
    } catch (error) {
      setNotification({ message: 'An error occurred', type: 'error' });
    }
  };

  const generateTemplate = async () => {
    if (!formData.employee_id || !formData.previous_salary || !formData.new_salary || !formData.effective_date) {
      setNotification({ message: 'Please fill in Employee, Previous Salary, New Salary, and Effective Date first', type: 'error' });
      return;
    }

    setGeneratingPreview(true);
    try {
      const employee = employees.find(e => e.id === formData.employee_id);
      const tempData = {
        ...formData,
        employee_name: employee?.full_name || null,
        position: employee?.position || null,
      };

      const result = await incrementLettersService.createIncrementLetter({
        ...tempData,
        content: '' // Let backend generate
      });

      console.log('Generate template result:', result);

      if (result?.success && result.data?.content) {
        setFormData(prev => ({ ...prev, content: result.data.content }));
        setNotification({ message: 'Professional template generated successfully', type: 'success' });
        // Delete the temporary letter
        if (result.data.id) {
          await incrementLettersService.deleteIncrementLetter(result.data.id);
        }
      } else {
        const errorMsg = result?.error || result?.message || 'Failed to generate template';
        console.error('Template generation failed:', errorMsg, result);
        setNotification({ message: errorMsg, type: 'error' });
      }
    } catch (error) {
      console.error('Error generating template:', error);
      const errorMsg = error?.message || String(error) || 'Error generating template';
      setNotification({ message: errorMsg, type: 'error' });
    } finally {
      setGeneratingPreview(false);
    }
  };

  const handlePreview = async () => {
    if (!formData.employee_id || !formData.previous_salary || !formData.new_salary || !formData.effective_date) {
      setNotification({ message: 'Please fill in Employee, Previous Salary, New Salary, and Effective Date first', type: 'error' });
      return;
    }

    setGeneratingPreview(true);
    try {
      const employee = employees.find(e => e.id === formData.employee_id);
      const tempData = {
        ...formData,
        employee_name: employee?.full_name || null,
        position: employee?.position || null,
      };

      const result = await incrementLettersService.createIncrementLetter({
        ...tempData,
        content: '' // Force regeneration to get fresh company data
      });

      console.log('Preview result:', result);

      if (result?.success && result.data?.content) {
        const content = result.data.content || '';
        setPreviewContent(content);
        
        // Create blob URL to avoid source map errors with srcDoc
        const cleanContent = content
          .replace(/\/\*# sourceMappingURL=.*?\*\//g, '') // Remove CSS source maps
          .replace(/\/\/# sourceMappingURL=.*?$/gm, '') // Remove JS source maps
          .replace(/<script[^>]*>[\s\S]*?<\/script>/gi, ''); // Remove script tags
        
        const blob = new Blob([cleanContent], { type: 'text/html' });
        const url = URL.createObjectURL(blob);
        setPreviewUrl(url);
        setShowPreview(true);
        // Delete temporary letter
        if (result.data.id) {
          await incrementLettersService.deleteIncrementLetter(result.data.id);
        }
      } else {
        const errorMsg = result?.error || result?.message || 'Failed to generate preview';
        console.error('Preview generation failed:', errorMsg, result);
        setNotification({ message: errorMsg, type: 'error' });
      }
    } catch (error) {
      console.error('Error generating preview:', error);
      const errorMsg = error?.message || String(error) || 'Error generating preview';
      setNotification({ message: errorMsg, type: 'error' });
    } finally {
      setGeneratingPreview(false);
    }
  };

  const handleDownload = async (id) => {
    try {
      const result = await incrementLettersService.downloadIncrementLetter(id);
      if (result?.success) {
        setNotification({ message: 'Increment letter downloaded successfully', type: 'success' });
      } else {
        setNotification({ message: 'Error downloading increment letter', type: 'error' });
      }
    } catch (error) {
      setNotification({ message: 'Error downloading increment letter', type: 'error' });
    }
  };

  return (
    <div className="p-6">
      <NotificationToast message={notification?.message} type={notification?.type} onDismiss={() => setNotification(null)} />
      
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-semibold text-foreground">Increment Letters</h2>
          <p className="text-sm text-muted-foreground mt-1">Handle salary increment documentation</p>
        </div>
        <Button onClick={() => { setEditingLetter(null); setFormData({ employee_id: '', previous_salary: '', new_salary: '', increment_percentage: '', effective_date: '', reason: '', content: '', status: 'draft' }); setShowModal(true); }}>
          <Icon name="Plus" size={16} className="mr-2" />
          Create Increment Letter
        </Button>
      </div>

      {loading ? (
        <div className="text-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4" /><p className="text-muted-foreground">Loading...</p></div>
      ) : (
        <div className="bg-background border border-border rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-muted">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-foreground uppercase">Employee</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-foreground uppercase">Previous Salary</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-foreground uppercase">New Salary</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-foreground uppercase">Increment %</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-foreground uppercase">Effective Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-foreground uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-foreground uppercase">Created</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-foreground uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {letters.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="px-6 py-12 text-center text-muted-foreground">
                      <Icon name="FileText" size={48} className="mx-auto mb-4 opacity-50" />
                      <p className="text-lg font-medium mb-2">No increment letters found</p>
                      <p className="text-sm">Create your first increment letter using the button above</p>
                    </td>
                  </tr>
                ) : (
                  letters.map(letter => (
                    <tr key={letter.id} className="hover:bg-muted/30 transition-colors">
                      <td className="px-6 py-4">
                        <div className="font-medium text-foreground">{letter.employee_name || 'N/A'}</div>
                        {letter.employee_email && (
                          <div className="text-sm text-muted-foreground">{letter.employee_email}</div>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm text-foreground">
                        ₹{parseFloat(letter.previous_salary || 0).toLocaleString('en-IN')}
                      </td>
                      <td className="px-6 py-4 text-sm text-foreground font-medium">
                        ₹{parseFloat(letter.new_salary || 0).toLocaleString('en-IN')}
                      </td>
                      <td className="px-6 py-4">
                        <span className="px-2 py-1 rounded text-xs font-medium bg-green-100 text-green-800">
                          {parseFloat(letter.increment_percentage || 0).toFixed(2)}%
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-foreground">
                        {letter.effective_date ? new Date(letter.effective_date).toLocaleDateString('en-IN') : '-'}
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          letter.status === 'draft' ? 'bg-gray-100 text-gray-800' :
                          letter.status === 'sent' ? 'bg-blue-100 text-blue-800' :
                          letter.status === 'accepted' ? 'bg-green-100 text-green-800' :
                          'bg-red-100 text-red-800'
                        }`}>
                          {letter.status?.charAt(0).toUpperCase() + letter.status?.slice(1) || 'Draft'}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-muted-foreground">
                        {letter.created_at ? new Date(letter.created_at).toLocaleDateString('en-IN') : '-'}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex gap-1">
                          <Button variant="ghost" size="sm" onClick={() => handleDownload(letter.id)} title="Download">
                            <Icon name="Download" size={14} />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleEdit(letter)} title="Edit">
                            <Icon name="Edit" size={14} />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDelete(letter.id)} className="text-destructive" title="Delete">
                            <Icon name="Trash2" size={14} />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => { setShowModal(false); setEditingLetter(null); }}>
          <div className="bg-background rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold mb-4">{editingLetter ? 'Edit' : 'Create'} Increment Letter</h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Employee *</label>
                <select value={formData.employee_id} onChange={(e) => setFormData({...formData, employee_id: e.target.value})} className="w-full px-3 py-2 border border-border rounded-md" required>
                  <option value="">Select Employee</option>
                  {employees.map(emp => <option key={emp.id} value={emp.id}>{emp.full_name}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <Input label="Previous Salary *" type="number" value={formData.previous_salary} onChange={(e) => setFormData({...formData, previous_salary: e.target.value})} required />
                <Input label="New Salary *" type="number" value={formData.new_salary} onChange={(e) => setFormData({...formData, new_salary: e.target.value})} required />
                <Input label="Increment %" type="number" step="0.01" value={formData.increment_percentage} readOnly className="bg-muted" />
                <Input label="Effective Date *" type="date" value={formData.effective_date} onChange={(e) => setFormData({...formData, effective_date: e.target.value})} required />
              </div>
              <textarea value={formData.reason} onChange={(e) => setFormData({...formData, reason: e.target.value})} className="w-full px-3 py-2 border border-border rounded-md" rows="2" placeholder="Reason for increment" />
              <div>
                <label className="block text-sm font-medium mb-2">Letter Content</label>
                <div className="flex gap-2 mb-2">
                  <Button type="button" variant="outline" size="sm" onClick={generateTemplate} disabled={generatingPreview}>
                    <Icon name="FileText" size={14} className="mr-2" />
                    {generatingPreview ? 'Generating...' : 'Generate Template'}
                  </Button>
                  <Button type="button" variant="outline" size="sm" onClick={handlePreview} disabled={generatingPreview}>
                    <Icon name="Eye" size={14} className="mr-2" />
                    Preview
                  </Button>
                </div>
                <textarea value={formData.content} onChange={(e) => setFormData({...formData, content: e.target.value})} className="w-full px-3 py-2 border border-border rounded-md" rows="6" placeholder="Click 'Generate Template' to create a professional increment letter template, or write your own content. You can edit the generated template as needed." />
              </div>
              <div className="flex gap-3 justify-end">
                <Button type="button" variant="outline" onClick={() => { setShowModal(false); setEditingLetter(null); }}>Cancel</Button>
                <Button type="submit" disabled={generatingPreview}>
                  {generatingPreview ? 'Saving...' : 'Save Increment Letter'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showPreview && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => { setShowPreview(false); if (previewUrl && previewUrl.startsWith('blob:')) { URL.revokeObjectURL(previewUrl); setPreviewUrl(''); } }}>
          <div className="bg-background rounded-lg p-6 max-w-5xl w-full mx-4 max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Increment Letter Preview</h3>
              <Button variant="ghost" size="sm" onClick={() => { setShowPreview(false); if (previewUrl && previewUrl.startsWith('blob:')) { URL.revokeObjectURL(previewUrl); setPreviewUrl(''); } }}>
                <Icon name="X" size={16} />
              </Button>
            </div>
            <div className="flex-1 overflow-auto border border-border rounded">
              <iframe
                src={previewUrl}
                className="w-full h-full min-h-[600px] border-0"
                title="Increment Letter Preview"
                sandbox="allow-same-origin"
                id="increment-letter-iframe"
              />
            </div>
            <div className="mt-4 flex gap-3 justify-end">
              <Button variant="outline" onClick={() => { setShowPreview(false); if (previewUrl && previewUrl.startsWith('blob:')) { URL.revokeObjectURL(previewUrl); setPreviewUrl(''); } }}>Close</Button>
              <Button onClick={() => {
                // Open content in new window for printing (cleaner than iframe print)
                if (previewContent) {
                  const printWindow = window.open('', '_blank');
                  if (printWindow) {
                    // Clean content for printing
                    const cleanContent = previewContent
                      .replace(/\/\*# sourceMappingURL=.*?\*\//g, '')
                      .replace(/\/\/# sourceMappingURL=.*?$/gm, '')
                      .replace(/<script[^>]*>[\s\S]*?<\/script>/gi, '');
                    
                    // Add print-specific styles
                    const contentWithScript = cleanContent.replace(
                      '</head>',
                      `<style>
                        @media print {
                          body {
                            margin: 0;
                            padding: 0;
                          }
                        }
                      </style>
                    </head>`
                    );
                    
                    printWindow.document.write(contentWithScript);
                    printWindow.document.close();
                    printWindow.focus();
                    printWindow.onload = () => {
                      setTimeout(() => {
                        printWindow.print();
                      }, 500);
                    };
                  }
                } else {
                  // Fallback: try iframe print
                  const iframe = document.getElementById('increment-letter-iframe');
                  if (iframe && iframe.contentWindow) {
                    try {
                      iframe.contentWindow.focus();
                      iframe.contentWindow.print();
                    } catch (e) {
                      console.error('Error printing iframe:', e);
                    }
                  }
                }
              }}>
                <Icon name="Printer" size={16} className="mr-2" />
                Print / Save as PDF
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default IncrementLetters;

